// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import {
    bindActionCreators,
    Dispatch,
} from 'redux';
import { connect } from 'react-redux';
import {
    getStatus,
    IComputeResourceResponse,
} from 'common/api/resources/ComputeResource';
import { COMPUTE_RESOURCE_STATUS } from 'admin/computeResource/constants';
import ComputeResourceConfigureNetwork from 'admin/computeResource/list/containers/ComputeResourceConfigureNetwork';
import ComputeResourceInstall from 'admin/computeResource/list/containers/ComputeResourceInstall';
import * as computeResourceActions from 'admin/computeResource/actions';
import { FormInstanceHandles } from '@plesk/ui-library';
import { FIELDS } from 'admin/computeResource/list/containers/ComputeResourceForm/ComputeResourceForm';
import { track } from 'common/analytics/mixpanel';
import { ANALYTICS_EVENTS } from 'common/analytics/constants';

interface IComputeResourceProps {
    networkFormRef?: React.RefObject<FormInstanceHandles>;
    onClose: () => void;
    item: IComputeResourceResponse;
    buttons?: boolean;
    hideUnavailable?: boolean;
    fields?: FIELDS[];
    /** Called with the id the form created or retried, so the opener can follow it. */
    onCreated?: (id: number) => void;
}

export type ComputeResourceProps =
    IComputeResourceProps &
    ReturnType<typeof mapDispatchToProps>;

export const ComputeResource: React.FC<ComputeResourceProps> = ({
    item,
    onClose,
    computeResourceActions: { unsetItem },
    buttons = true,
    hideUnavailable = false,
    networkFormRef,
    fields,
    onCreated,
}) => {

    React.useEffect(() => {
        track(ANALYTICS_EVENTS.ADMIN.COMPUTE_RESOURCE.FORM_VISIT);
    }, []);

    React.useEffect(() => () => {
        unsetItem();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (getStatus(item.status) === COMPUTE_RESOURCE_STATUS.CONFIGURE_NETWORK) {
        return (
            <ComputeResourceConfigureNetwork
                ref={networkFormRef}
                onConfigured={onClose}
                submitButton={buttons}
                hideUnavailable={hideUnavailable}
            />
        );
    }

    return (
        <ComputeResourceInstall
            item={item}
            fields={fields}
            onCreated={onCreated}
        />);
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
    computeResourceActions: bindActionCreators(computeResourceActions, dispatch),
});

export default connect(null, mapDispatchToProps)(ComputeResource);
